import { filter, includes, omit, orderBy, reject, toNumber } from "lodash";
import { DimensionWithScores, PersonScore } from "@/features/people";
import {
  RadarGroup,
  RadarSector,
  RadarScoreData,
  RadarAxisLabel,
} from "@/shared/widgets/Charts/RadarChart";
import { TFunction } from "i18next";
import { DimensionContext } from "@/types/data";

/**
 * Multi-person contexts that should display average scores and multiple data layers.
 * These contexts represent team or comparison views where multiple people's data is shown.
 */
const MULTI_PERSON_CONTEXTS: DimensionContext[] = [
  "user_in_team",
  "team",
  "user_comparison",
  "candidate",
];

/**
 * Determines if the given context represents a multi-person view (team/comparison).
 * @param context - The dimension context
 * @param omitCandidate - Whether to exclude the candidate context from multi-person views. For example showing scores on charts
 * @returns true if the context should show multiple people/average scores
 */
const isMultiPersonContext = (
  context: DimensionContext,
  omitCandidate?: boolean
): boolean => {
  let compareContext = MULTI_PERSON_CONTEXTS;

  if (omitCandidate) {
    compareContext = reject(
      MULTI_PERSON_CONTEXTS,
      (v) => v === "candidate"
    ) as DimensionContext[];
  }

  return includes(compareContext, context);
};

/**
 * Ordering configuration for groups and sectors
 * Maps dimension title patterns to their clockwise order
 *
 * Quadrant layout (starting from right = 0°):
 * - Position 0 (0-90°): Right to bottom (Self → Freedom)
 * - Position 1 (90-180°): Bottom to left (Freedom → Other)
 * - Position 2 (180-270°): Left to top (Other → Control)
 * - Position 3 (270-360°): Top to right (Control → Self)
 */
interface OrderConfig {
  pattern: string;
  order: number;
}

const GROUP_ORDER_CONFIG: OrderConfig[] = [
  { pattern: "self-enhancement", order: 3 },
  { pattern: "self-direction", order: 0 },
  { pattern: "self-transcendence", order: 1 },
  { pattern: "self-restraint", order: 2 },
];

/**
 * Sector ordering within each group (clockwise direction)
 * Each group's sectors are ordered from start axis to end axis
 */
const SECTOR_ORDER_CONFIG: OrderConfig[] = [
  // Self-Enhancement sectors (clockwise: Control → Self)
  { pattern: "status", order: 1 },
  { pattern: "achievement", order: 2 },

  // Self-Direction sectors (clockwise: Self → Freedom)
  { pattern: "pleasure", order: 1 },
  { pattern: "change", order: 2 },
  { pattern: "curiosity", order: 3 },

  // Self-Transcendence sectors (clockwise: Freedom → Other)
  { pattern: "idealism", order: 1 },
  { pattern: "connection", order: 2 },

  // Self-Restraint sectors (clockwise: Other → Control)
  { pattern: "conformity", order: 1 },
  { pattern: "security", order: 2 },
];

/**
 * Axis labels positioned at quadrant boundaries
 * position: 0=right (0°), 1=bottom (90°), 2=left (180°), 3=top (270°)
 */
const AXIS_LABELS: RadarAxisLabel[] = [
  { label: "Self", position: 0 },
  { label: "Freedom", position: 1 },
  { label: "Other", position: 2 },
  { label: "Control", position: 3 },
];

/**
 * Result type for chart data transformation
 */
export interface RadarChartData {
  groups: RadarGroup[];
  axisLabels: RadarAxisLabel[];
  data: RadarScoreData[];
  /** Default/compound scores for aggregate display (e.g., team average) */
  defaultScores?: Record<string | number, number>;
}

/**
 * Find order based on title pattern matching
 */
const findOrderByPattern = (title: string, config: OrderConfig[]): number => {
  const normalizedTitle = title.toLowerCase().replace(/[\s_]/g, "-");
  const match = config.find((c) => normalizedTitle.includes(c.pattern));
  return match?.order ?? 99;
};

const findGroupOrder = (title: string): number =>
  findOrderByPattern(title, GROUP_ORDER_CONFIG);

const findSectorOrder = (title: string): number =>
  findOrderByPattern(title, SECTOR_ORDER_CONFIG);

/**
 * Transforms DimensionWithScores[] into RadarChart format
 */
export const getRadarChartData = ({
  dimensions,
  t,
  personId,
  context,
}: {
  dimensions: DimensionWithScores[];
  personId?: number;
  t?: TFunction;
  context: DimensionContext;
}): RadarChartData => {
  // Separate groups (no parentId) from sectors (has parentId)
  const groupDimensions = filter(dimensions, (d) => !d.parentId);
  const sectorDimensions = filter(dimensions, (d) => !!d.parentId);

  // Build groups with their sectors
  const unsortedGroups = groupDimensions.map((groupDim) => {
    const sectors = filter(sectorDimensions, (d) => d.parentId === groupDim.id);
    const sortedSectors = orderBy(
      sectors,
      (s) => findSectorOrder(s.title || s.code || ""),
      "asc"
    );

    const radarSectors: RadarSector[] = sortedSectors.map((sector) => {
      const shouldUseAverageScore = isMultiPersonContext(context, true);
      return {
        id: sector.id,
        label: sector.title || sector.code || "",
        score: toNumber(
          shouldUseAverageScore
            ? sector.averageScore?.value ?? 0
            : sector.score?.value ?? 0
        ),
      };
    });

    return {
      id: groupDim.id,
      label: groupDim.title || groupDim.code || "",
      sectors: radarSectors,
      order: findGroupOrder(groupDim.title || ""),
    };
  });

  // Sort groups by order
  const sortedGroups = orderBy(unsortedGroups, "order", "asc");

  // Extract groups without the order property
  const groups: RadarGroup[] = sortedGroups.map(({ order, ...group }) => group);

  // Build ordered sector list for score mapping
  const orderedSectorIds = groups.flatMap((g) => g.sectors.map((s) => s.id));

  const defaultScores: Record<string | number, number> = {};
  orderedSectorIds.forEach((sectorId) => {
    const dim = sectorDimensions.find((d) => d.id === sectorId);
    const shouldUseAverageScore = isMultiPersonContext(context);
    defaultScores[sectorId] = toNumber(
      shouldUseAverageScore
        ? dim?.averageScore?.value ?? 0
        : dim?.score?.value ?? 0
    );
  });

  // Extract member scores based on context
  const data = extractDataLayers(
    sectorDimensions,
    orderedSectorIds,
    defaultScores,
    context
  );

  // Translate axis labels if translation function provided
  const axisLabels = t
    ? AXIS_LABELS.map((label) => ({
        ...label,
        label: t(
          `profile.values_chart.axis.${label.label.toLowerCase()}`,
          label.label
        ),
      }))
    : AXIS_LABELS;

  // Include defaultScores for multi-layer mode (compound/aggregate display)
  const hasMultipleLayers = isMultiPersonContext(context);

  return {
    groups,
    axisLabels,
    data,
    // Only include defaultScores if we have multiple data layers
    ...(hasMultipleLayers && { defaultScores }),
  };
};

const extractDataLayers = (
  sectorDimensions: DimensionWithScores[],
  orderedSectorIds: (string | number)[],
  defaultScores: Record<string | number, number>,
  context: DimensionContext
): RadarScoreData[] => {
  // Find first dimension with people scores
  const dimWithScores = sectorDimensions.find(
    (d) => d.peopleScores && d.peopleScores.length > 0
  );

  // No people scores at all - return default layer without indicator
  if (!dimWithScores?.peopleScores || dimWithScores.peopleScores.length === 0) {
    return [
      {
        id: "default",
        scores: defaultScores,
      },
    ];
  }

  const isMultiLayerMode = isMultiPersonContext(context);

  // Single layer mode (individual view or only one person available)
  if (!isMultiLayerMode) {
    const ps = dimWithScores.peopleScores[0];
    const person = ps.person;

    return [
      {
        id: person?.id || "default",
        scores: defaultScores,
        indicator: person
          ? {
              avatar: person.avatar || "",
              initials: person.initials || "",
            }
          : undefined,
      },
    ];
  }

  // Multi-layer mode - create layer for each person
  const dataLayers: RadarScoreData[] = [];

  dimWithScores.peopleScores.forEach((ps: PersonScore) => {
    if (!ps.person?.id) return;

    const personId = ps.person.id;
    const scores: Record<string | number, number> = {};

    orderedSectorIds.forEach((sectorId) => {
      const dim = sectorDimensions.find((d) => d.id === sectorId);
      const personScore = dim?.peopleScores?.find(
        (p: PersonScore) => p.person?.id === personId
      );
      scores[sectorId] = toNumber(personScore?.value ?? 0);
    });

    dataLayers.push({
      id: personId,
      scores,
      indicator: {
        avatar: ps.person.avatar || "",
        initials: ps.person.initials || "",
      },
    });
  });

  return dataLayers;
};

/**
 * Gets a dimension by ID from the dimensions array
 */
export const getDimensionById = (
  dimensions: DimensionWithScores[],
  id: string | number
): DimensionWithScores | undefined => {
  return dimensions.find((d) => d.id === toNumber(id));
};
